import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { shortDate } from "@/lib/api";
import { Bell, CheckCircle, XCircle, PencilSimpleLine, Circle, HourglassMedium, Warning, Coins } from "@phosphor-icons/react";

const TYPE_ICON = {
  edit_request_new: PencilSimpleLine,
  edit_request_approved: CheckCircle,
  edit_request_rejected: XCircle,
  maturity_soon: HourglassMedium,
  emi_due_soon: Coins,
  emi_overdue: Warning,
  daily_collect_pending: Coins,
};

const TYPE_TONE = {
  edit_request_new: "text-amber-600 bg-amber-50",
  edit_request_approved: "text-emerald-600 bg-emerald-50",
  edit_request_rejected: "text-rose-600 bg-rose-50",
  maturity_soon: "text-amber-700 bg-amber-100",
  emi_due_soon: "text-sky-700 bg-sky-100",
  emi_overdue: "text-rose-700 bg-rose-100",
  daily_collect_pending: "text-amber-700 bg-amber-100",
};

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([
        api.get("/notifications/unread-count"),
        api.get("/notifications?limit=20"),
      ]);
      setCount(c.data.count || 0);
      setItems(l.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onChanged = () => load();
    window.addEventListener("edit-requests-changed", onChanged);
    window.addEventListener("notifications-changed", onChanged);
    return () => {
      clearInterval(t);
      window.removeEventListener("edit-requests-changed", onChanged);
      window.removeEventListener("notifications-changed", onChanged);
    };
  }, [load]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const onClickItem = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        window.dispatchEvent(new Event("notifications-changed"));
      } catch { /* ignore */ }
    }
    setOpen(false);
    if (n.link) nav(n.link);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      window.dispatchEvent(new Event("notifications-changed"));
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notif-bell"
        className="relative w-9 h-9 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-600 transition-colors"
        title="Notifications"
      >
        <Bell size={18} weight={count > 0 ? "fill" : "regular"} />
        {count > 0 && (
          <span
            data-testid="notif-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-semibold flex items-center justify-center"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 max-h-[70vh] bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden flex flex-col z-50 fade-up"
          data-testid="notif-panel"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="heading font-semibold text-sm">Notifications</div>
            {count > 0 && (
              <button
                onClick={markAll}
                className="text-xs text-emerald-700 hover:underline"
                data-testid="notif-mark-all"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-10 text-center text-sm text-slate-500">No notifications yet.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map((n) => {
                  const Icon = TYPE_ICON[n.type] || Bell;
                  const tone = TYPE_TONE[n.type] || "text-slate-500 bg-slate-100";
                  return (
                    <li key={n.id}>
                      <button
                        onClick={() => onClickItem(n)}
                        data-testid={`notif-item-${n.id}`}
                        className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors ${
                          !n.read ? "bg-emerald-50/30" : ""
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${tone}`}>
                          <Icon size={16} weight="fill" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900">{n.title}</div>
                          {n.body && <div className="text-xs text-slate-500 mt-0.5 truncate">{n.body}</div>}
                          <div className="text-[10px] text-slate-400 mt-1 mono">{shortDate(n.created_at)}</div>
                        </div>
                        {!n.read && (
                          <Circle size={8} weight="fill" className="text-emerald-500 shrink-0 mt-2" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
